import PageTransition from '@/components/layout/page-transition';
import { SEOProps } from '@/components/ui/SEO';
import Layout from '@/layout/default';
import { AppProvider } from '@/providers/root';
import { usePerformance } from '@/providers/performance.provider';
import '@/styles/main.scss';
import '@/styles/tailwind.css';
import { AnimatePresence } from 'framer-motion';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLenis } from 'lenis/react';
import type { AppProps } from 'next/app';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

type AppPropsWithSEO = AppProps & {
  Component: AppProps['Component'] & {
    seo?: SEOProps;
  };
  pageProps: AppProps['pageProps'] & { seo?: SEOProps };
};

function scrollToAnchorWithLenis(lenis: ReturnType<typeof useLenis>, hash: string, offset = 96) {
  const element = document.getElementById(hash);
  if (!element) return;

  if (lenis) {
    lenis.resize();
    lenis.scrollTo(element, { offset: -offset, immediate: false });
  } else {
    const targetY = element.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: targetY, behavior: 'smooth' });
  }
}

function AppInner({ Component, pageProps }: AppPropsWithSEO) {
  const lenis = useLenis();
  const pathname = usePathname();
  const { isLoading } = usePerformance();
  const [isFirstVisit, setIsFirstVisit] = useState(true);
  const anchorRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoading) {
      const id = setTimeout(() => setIsFirstVisit(false), 0);
      return () => clearTimeout(id);
    }
  }, [isLoading]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash.replace('#', '');
    anchorRef.current = hash || null;
  }, [pathname]);

  return (
    <Layout seoProps={Component.seo ?? pageProps.seo}>
      <AnimatePresence
        mode="wait"
        onExitComplete={() => {
          if (lenis) {
            lenis.scrollTo(0, { immediate: true });
          } else {
            window.scrollTo(0, 0);
          }
          requestAnimationFrame(() => {
            ScrollTrigger.refresh();
            const hash = anchorRef.current;
            if (!hash) return;

            setTimeout(() => {
              scrollToAnchorWithLenis(lenis, hash);
              anchorRef.current = null;
            }, 100);
          });
        }}
      >
        <PageTransition key={pathname} skipEnterAnimation={isFirstVisit}>
          <Component {...pageProps} />
        </PageTransition>
      </AnimatePresence>
    </Layout>
  );
}

function App(props: AppProps) {
  return (
    <AppProvider>
      <AppInner {...props} />
    </AppProvider>
  );
}

export default App;
